import { useAtomValue } from 'jotai';
import { logHistoryAtom } from 'stores/jotai.store';
import { useState } from 'react';
import {
  filterLogHistory,
  logLevelFilter,
  LogLevelFilterValueType,
  logOrderFilter,
  LogOrderFilterValueType
} from './dev-tools-logs.function';
import logStyles from './logs.module.scss';
import { DevToolsModal } from 'dev-tools/components/dev-tools-modal/DevToolsModal';
import { PrettyPrintJson } from 'dev-tools/components/pretty-print-json/PrettyPrintJson';
import { displayToast } from '@sanlam-glacier/app-common/modules/toasts';
import { LogMessageWithMeta } from 'types/logging/meta-log.type';

export const DevToolsLogs = () => {
  const logHistory = useAtomValue(logHistoryAtom);
  const [logLevel, setLogLevel] = useState<LogLevelFilterValueType>('all');
  const [logOrder, setLogOrder] = useState<LogOrderFilterValueType>('new-to-old');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [logToDisplay, setLogToDisplay] = useState<object | undefined>();

  const copyLog = async (log: LogMessageWithMeta) => {
    await navigator.clipboard.writeText(JSON.stringify(log));
    displayToast({
      toastId: `dev-tools-logs-${log.message}-${log.timestamp.toISOString()}`,
      title: 'Log Copied',
      message: 'The log was copied to the clipboard',
      status: 'info'
    });
  };

  const renderLogRow = (log: LogMessageWithMeta) => {
    return (
      <tr>
        <td className={`${logStyles.logsRowLevel}.${log.level}`}>{log.level}</td>
        <td>{log.timestamp.toLocaleString()}</td>
        <td>{log.message}</td>
        <td>{log.correlationId ?? '-'}</td>
        <td>
          <button
            onClick={() => {
              setLogToDisplay(log.object);
              setIsModalOpen(true);
            }}
          >
            View
          </button>
        </td>
        <td>
          <button onClick={() => copyLog(log)}>Copy</button>
        </td>
      </tr>
    );
  };

  const handleLogLevelFilterChange = (value: LogLevelFilterValueType) => {
    setLogLevel(value);
  };

  const handleLogOrderFilterChange = (value: LogOrderFilterValueType) => {
    setLogOrder(value);
  };

  return (
    <>
      <DevToolsModal
        isOpen={isModalOpen}
        title='Log Details'
        onClose={() => {
          setIsModalOpen(false);
          setLogToDisplay(undefined);
        }}
      >
        {logToDisplay && <PrettyPrintJson jsonData={logToDisplay} />}
      </DevToolsModal>
      <div className={logStyles.logsControls}>
        <div className={logStyles.logsControlsItem}>
          <p>Sorting</p>
          <select
            value={logOrder}
            onChange={(event) => {
              const eventValue = (event.target.value ?? 'new-to-old') as LogOrderFilterValueType;
              handleLogOrderFilterChange(eventValue);
            }}
          >
            {logOrderFilter.map((filter) => (
              <option key={filter.value} value={filter.value}>
                {filter.displayValue}
              </option>
            ))}
          </select>
        </div>
        <div className={logStyles.logsControlsItem}>
          <p>Type</p>
          <select
            value={logLevel}
            onChange={(event) => {
              const eventValue = (event.target.value ?? 'all') as LogLevelFilterValueType;
              handleLogLevelFilterChange(eventValue);
            }}
          >
            {logLevelFilter.map((filter) => (
              <option key={filter.value} value={filter.value}>
                {filter.displayValue}
              </option>
            ))}
          </select>
        </div>
      </div>
      <table className={logStyles.logsTable}>
        <thead>
          <tr>
            <th>Level</th>
            <th>Time</th>
            <th>Message</th>
            <th>CorrelationId</th>
            <th>Object</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>{filterLogHistory(logHistory, logOrder, logLevel).map(renderLogRow)}</tbody>
      </table>
    </>
  );
};
